import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  RichTextEditorInputWithMention,
} from "@/components";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import { useCreateCompanyComment } from "@/hooks/mutations/useCreateCompanyComment";
import { useAddCompanyComment } from "@/hooks/sets/useAddCompanyComment";
import { tiptapDefaultJson } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

const createCompanyCommentSchema = z.object({
  content: z
    .object({
      type: z.literal("doc"),
      content: z.array(z.any()),
    })
    .refine(
      (data) => {
        return (
          JSON.stringify(data.content) !==
          JSON.stringify(tiptapDefaultJson.content)
        );
      },
      {
        message: "Required",
      },
    ),
});

export const CreateCompanyCommentForm = () => {
  const addCompanyComment = useAddCompanyComment();
  const {
    mutate: createCompanyComment,
    isPending: isCreateCompanyCommentPending,
    isError: isCreateCompanyCommentError,
  } = useCreateCompanyComment();

  const form = useForm<z.infer<typeof createCompanyCommentSchema>>({
    defaultValues: {
      content: tiptapDefaultJson,
    },
    resolver: zodResolver(createCompanyCommentSchema),
  });

  function onSubmit(data: z.infer<typeof createCompanyCommentSchema>) {
    mixpanelUtils.trackButtonClick("Add comment", MIXPANEL_SCREENS.COMPANY);
    createCompanyComment(
      {
        createCompanyCommentRequest: { content: data.content },
      },
      {
        onSuccess: (newCompanyComment) => {
          form.reset();
          addCompanyComment({
            companyCommentToAdd: newCompanyComment,
          });
        },
      },
    );
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RichTextEditorInputWithMention
                  editorContentClasses="pb-20"
                  onChange={field.onChange}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Column className="items-end gap-2">
          <Button
            className="relative rounded-full"
            disabled={isCreateCompanyCommentPending}
            type="submit"
            variant="outline"
          >
            <span className={isCreateCompanyCommentPending ? "invisible" : ""}>
              Add
            </span>
            {isCreateCompanyCommentPending && (
              <LoadingIndicator
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                variant="small"
              />
            )}
          </Button>
          {isCreateCompanyCommentError && (
            <ErrorText errorMessage="An error occurred while adding comment. Please try again." />
          )}
        </Column>
      </form>
    </Form>
  );
};
